/**
 * 全站路由配置
 *
 * 建议:
 * 1. 代码中路由统一使用name属性跳转(不使用path属性)
 */
import Vue from "vue";
import Router from "vue-router";
import { isURL } from "@/utils/validate";
import loginHttp, { checkLogin, logout } from "@/utils/login";

Vue.use(Router);

// 开发环境不使用懒加载, 因为懒加载页面太多的话会造成webpack热更新太慢, 所以只有生产环境使用懒加载
const _import = require("./import-" + process.env.NODE_ENV);

const skipLoadMenusRoutes = [{ name: "demo", path: "/demo" }];
/**
 * 重写路由的push方法
 */
const routerPush = Router.prototype.push;
const addRouter = Router.prototype.addRouter;
Router.prototype.push = function push(location) {
  return routerPush.call(this, location).catch((error) => error);
};
// 全局路由(无需嵌套上左右整体布局)
const globalRoutes = [
  {
    path: "/404",
    component: _import("common/404"),
    name: "404",
    meta: { title: "404未找到" },
  },
  {
    path: "/error/:msg?",
    component: _import("common/error"),
    name: "error",
    meta: { title: "系统错误" },
  },
  {
    path: "/login",
    component: _import("common/login"),
    name: "login",
    meta: { title: "登录" },
    redirect: "/loginForm",
    children: [
      {
        path: "/loginForm",
        component: _import("common/loginForm"),
        name: "loginForm",
      },
      { path: "/forget", component: _import("common/forget"), name: "forget" },
    ],
  },
  {
    path: "/redirect",
    component: _import("common/redirectPage"),
    name: "redirect",
    meta: { title: "跳转见证", isTab: false },
  },
  {
    path: "/bid/bigScreen",
    component: _import("common/bigScreen"),
    name: "bidScreen",
    // isAuth 表示要校验登录及权限
    meta: { title: "评标区域数字见证大数据", isAuth: true },
  },
];
// 主入口路由(需嵌套上左右整体布局)
const mainRoutes = {
  path: "/",
  component: _import("main"),
  name: "main",
  redirect: { name: "home" },
  meta: { title: "主入口整体布局" },
  children: [
    // 通过meta对象设置路由展示方式
    // 1. isTab: 是否通过tab展示内容, true: 是, false: 否
    // 2. iframeUrl: 是否通过iframe嵌套展示内容, '以http[s]://开头': 是, '': 否
    // 提示: 如需要通过iframe嵌套展示内容, 但不通过tab打开, 请自行创建组件使用iframe处理!
    {
      path: "/home",
      component: _import("common/home"),
      name: "home",
      meta: { single: true, title: "首页" },
    },
    {
      path: "/theme",
      component: _import("common/theme"),
      name: "theme",
      meta: { single: true, title: "主题", isTab: true },
    },
    //元数据列表&详页
    {
      path: "/metadata/table_list",
      component: _import("modules/metadata/table_list"),
      name: "metadata_table_list",
      meta: { single: true, title: "元数据管理", isTab: true },
    },
    {
      path: "/metadata/table_config/:id",
      component: _import("modules/metadata/table_config"),
      name: "metadata_table_config",
      meta: { single: true, title: "元数据配置", isTab: true },
    },
    //自定义表单管理列表&详页
    {
      path: "/customfm/detailconfig/:id",
      component: _import("modules/customform/detail_config"),
      name: "customform_detail_config",
      meta: { single: true, title: "自定义表单明细配置", isTab: true },
    },
    {
      path: "/customfm/listconfig/:id",
      component: _import("modules/customform/list_config"),
      name: "customform_list_config",
      meta: { single: true, title: "列表表单配置", isTab: true },
    },
    {
      path: "/customfm/list",
      component: _import("modules/customform/list"),
      name: "customform_list",
      meta: { single: true, title: "自定义表单管理", isTab: true },
    },
    {
      path: "/customfm/edit",
      component: _import("modules/customform/edit"),
      name: "customform_edit",
      meta: { single: true, title: "自定义表单配置", isTab: true },
    },
    //数据库连接配置
    {
      path: "/datasourceconfig/list",
      component: _import("modules/datasourceconfig/list"),
      name: "datasourceconfig_list",
      meta: { single: true, title: "数据源列表", isTab: true },
    },
    {
      path: "/datasourceconfig/edit",
      component: _import("modules/datasourceconfig/edit"),
      name: "datasourceconfig_edit",
      meta: { single: true, title: "数据源详情", isTab: true },
    },
    //数据库连接配置
    {
      path: "/test/list/:formId",
      component: _import("modules/test/list"),
      name: "test_list",
      meta: { single: true, title: "自定义表单测试", isTab: true },
    },
    {
      path: "/test/detail/:formId/:id",
      component: _import("modules/test/detail"),
      name: "test_detail",
      meta: { single: true, title: "自定义表单测试明细", isTab: true },
    },
    {
      path: "/test/video",
      component: _import("demo/video_demo"),
      name: "test_video",
      meta: { single: true, title: "视频直播测试", isTab: true },
    },
    // 场地摄像头配置
    {
      path: "/cameras_config/list",
      component: _import("modules/config/cameras_config_list"),
      name: "cameras_config_list",
      meta: { single: true, title: "场地摄像头配置", isTab: true },
    },
    // 预警规则配置
    {
      path: "/warnItem/witness_warn_notice",
      component: _import("modules/warnItem/witness_warn_notice"),
      name: "witness_warn_notice",
      meta: { single: true, title: "交易预警通知", isTab: true },
    },
    {
      path: "/warnItem/warn_items_list",
      component: _import("modules/warnItem/warn_items_list"),
      name: "warn_items_list",
      meta: { single: true, title: "预警规则配置", isTab: true },
    },
    {
      path: "/warn_record/list",
      component: _import("modules/warnrecord/warn_record_list"),
      name: "warn_record_list",
      meta: { single: true, title: "交易数据预警列表", isTab: true },
    },
    {
      path: "/warn_record/detail",
      component: _import("modules/warnrecord/warn_record_detail"),
      name: "warn_record_detail",
      meta: { single: true, title: "交易数据预警详页", isTab: true },
    },
    {
      path: "/warn_record_activity/list",
      component: _import("modules/warnrecord/warn_record_activity_list"),
      name: "warn_record_activity_list",
      meta: { single: true, title: "交易活动预警列表", isTab: true },
    },
    {
      path: "/warn_record_activity/detail",
      component: _import("modules/warnrecord/warn_record_activity_detail"),
      name: "warn_record_activity_detail",
      meta: { single: true, title: "交易活动预警详页", isTab: true },
    },
    // { path: '/warnrecord/warn_record_form', component: _import('modules/warnrecord/warn_record_form'), name: 'warn_record_form', meta: {single:true, title: '交易数据查看', isTab: true } },
    // 业务流程节点配置
    {
      path: "/flownode_config/list",
      component: _import("modules/config/flownode_config_list"),
      name: "flownode_config_list",
      meta: { single: true, title: "业务流程节点配置列表", isTab: true },
    },
    {
      path: "/flownode_config/detail",
      component: _import("modules/config/flownode_config_detail"),
      name: "flownode_config_detail",
      meta: { single: true, title: "业务流程节点配置详页", isTab: true },
    },
    {
      path: "/flownode_config/view",
      component: _import("modules/config/flownode_config_view"),
      name: "flownode_config_view",
      meta: { single: true, title: "业务流程节点配置详页", isTab: true },
    },

    //专家抽取见证
    {
      path: "/traddingWitness/expertWitness_his/list",
      component: _import("modules/traddingWitness/expertWitness/list"),
      name: "expertWitness_list",
      meta: { single: true, title: "专家抽取见证列表", isTab: true },
    },
    {
      path: "/traddingWitness/expertWitness_his/view",
      component: _import("modules/traddingWitness/expertWitness/view"),
      name: "expertWitness_view",
      meta: { single: true, title: "专家抽取见证详页", isTab: true },
    },
    {
      path: "/traddingWitness/expertWitness/detail",
      component: _import("modules/traddingWitness/expertWitness/detail"),
      name: "expertWitness_detail",
      meta: { single: true, title: "专家抽取实时见证", isTab: true },
    },

    // 专家抽取
    {
      path: "/expertExtract",
      component: _import("modules/expertExtract/list"),
      name: "expertExtract",
      meta: { single: true, title: "专家抽取", isTab: true },
    },
    {
      path: "/expertExtract/detail",
      component: _import("modules/expertExtract/detail"),
      name: "extractSign",
      meta: { single: true, title: "专家抽取结果列表", isTab: true },
    },
    {
      path: "/expertExtract/review",
      component: _import("modules/expertExtract/expert_review"),
      name: "expert_review",
      meta: { single: true, title: "专家评审进度", isTab: true },
    },
    {
      path: "/expertExtract/review/detail",
      component: _import("modules/expertExtract/expert_review_detail"),
      name: "expert_review_detail",
      meta: { single: true, title: "专家评审进度详情", isTab: true },
    },
    {
      path: "/expertExtract/expert_wander",
      component: _import("modules/expertExtract/wander"),
      name: "expert_wander",
      meta: { single: true, title: "评审流程跳转核准", isTab: true },
    },
    {
      path: "/expertExtract/expert_wander_detail",
      component: _import("modules/expertExtract/expert_review_detail"),
      name: "expert_wander_detail",
      meta: { single: true, title: "评审流程跳转核准详情", isTab: true },
    },
    {
      path: "/expert_extract/scalingExpertInfo",
      component: _import("modules/expertExtract/scalingExpertInfo"),
      name: "scalingExpertInfo",
      meta: { single: true, title: "定标专家信息", isTab: true },
    },
    {
      path: "/expertExtract/scalingExpertInfo/detail",
      component: _import("modules/expertExtract/scalingExpertInfo_detail"),
      name: "scalingExpertInfoDetail",
      meta: { single: true, title: "定标专家信息详情", isTab: true },
    },
    {
      path: "/evaluationExpertpay/expertpayList",
      component: _import("modules/evaluationExpertpay/expertpayList"),
      name: "expertpayList",
      meta: { single: true, title: "专家费支付情况", isTab: true },
    },
    {
      path: "/evaluationExpertpay/expertpayDetail",
      component: _import("modules/evaluationExpertpay/expertpayDetail"),
      name: "expertpayDetail",
      meta: { single: true, title: "专家费支付详情", isTab: true },
    },
    {
      path: "/expertExtract/sign",
      component: _import("modules/expertExtract/detail"),
      // 审核人
      name: "extractSignRev",
      meta: { single: true, title: "专家抽取结果列表", isTab: true },
    },
    {
      path: "/expertExtract/extract_detail",
      component: _import("modules/expertExtract/extract_detail"),
      name: "expertExtract_detail",
      meta: { single: true, title: "专家抽取结果详页", isTab: true },
    },
    {
      path: "/expertExtract/extract_expert",
      component: _import("modules/expertExtract/extract_detail"),
      name: "expertExtract_expert_detail",
      meta: { single: true, title: "专家审核结果详页", isTab: true },
    },
    // 交易数据统计
    {
      path: "/transactionDataStatistics/projectConstruction",
      component: _import("modules/transactionDataStatistics/projectConstruction"),
      name: "projectConstruction",
      meta: { single: true, title: "工程建设交易情况统计", isTab: true },
    },
    // 交易数据统计-采购
    {
      path: "/transactionDataStatistics/projectConstructionCG",
      component: _import("modules/transactionDataStatistics/projectConstructionCG"),
      name: "projectConstructionCG",
      meta: { single: true, title: "政府采购交易情况统计", isTab: true },
    },
    //项目见证受理
    {
      path: "/businessflowWitness",
      redirect: { name: "busWitness_list_GC", params: { type: "GC" } },
    },
    {
      path: "/businessflowWitness_GC/list",
      component: _import("modules/businessflowWitness/list_gcjs"),
      name: "busWitness_list_GC",
      meta: { single: true, title: "见证受理-工程建设", isTab: true },
    },
    {
      path: "/businessflowWitness_CG/list",
      component: _import("modules/businessflowWitness/list_zfcg"),
      name: "busWitness_list_CG",
      meta: { single: true, title: "见证受理-政府采购", isTab: true },
    },
    {
      path: "/businessflowWitness_CQ/list",
      component: _import("modules/businessflowWitness/list_cqjy"),
      name: "busWitness_list_CQ",
      meta: { single: true, title: "见证受理-产权交易", isTab: true },
    },
    {
      path: "/businessflowWitness_GT/list",
      component: _import("modules/businessflowWitness/list_gtjy"),
      name: "busWitness_list_GT",
      meta: { single: true, title: "见证受理-国土交易", isTab: true },
    },
    {
      path: "/businessflowWitness/list_detail",
      component: _import("modules/businessflowWitness/busWitness_list_detail"),
      name: "busWitness_detail",
      meta: { single: true, title: "项目见证受理详页", isTab: true },
    },
    // {
    //   path: "/businessnodes/:id",
    //   component: _import("modules/businessnodes/businessNodesList"),
    //   name: "businessnodes_list",
    //   meta: { single: true, title: "项目注册", isTab: true },
    // },
    {
      path: "/businessnodes/list_detail",
      component: _import("modules/businessnodes/businessnodes_detail"),
      name: "businessnodes_detail",
      meta: { single: true, title: "项目业务节点详页", isTab: true },
    },

    //传输数据日志
    {
      path: "/sysTransLogs/list",
      component: _import("modules/logs/sysTransLogs"),
      name: "SysTransLogsTable",
      meta: { title: "传输数据日志列表", isTab: true },
    },
    {
      path: "/fieldMaintain/index",
      component: _import("modules/fieldMaintain/index"),
      name: "FieldMaintain",
      meta: { title: "场地信息维护", isTab: true },
    },
    {
      path: "/fieldMaintain/lists",
      component: _import("modules/fieldMaintain/lists"),
      name: "FieldMaintainLists",
      meta: { title: "场地信息维护列表", isTab: true },
    },

    //项目见证记录
    {
      path: "/businessflowWitness",
      redirect: { name: "businessWitnessHis_GC", params: { type: "GC" } },
    },
    {
      path: "/businessWitnessHis_GC/list",
      component: _import("modules/businessWitnessHis/list_gcjs"),
      name: "businessWitnessHis_GC",
      meta: { single: true, title: "项目全周期跟踪", isTab: true },
    },
    {
      path: "/businessWitnessHis_CG/list",
      component: _import("modules/businessWitnessHis/list_zfcg"),
      name: "businessWitnessHis_CG",
      meta: { single: true, title: "见证记录-政府采购", isTab: true },
    },
    {
      path: "/businessWitnessHis_CQ/list",
      component: _import("modules/businessWitnessHis/list_cqjy"),
      name: "businessWitnessHis_CQ",
      meta: { single: true, title: "见证记录-产权交易", isTab: true },
    },
    {
      path: "/businessWitnessHis_GT/list",
      component: _import("modules/businessWitnessHis/list_gtjy"),
      name: "businessWitnessHis_GT",
      meta: { single: true, title: "见证记录-国土交易", isTab: true },
    },
    {
      path: "/businessWitnessHis/list_detail",
      component: _import("modules/businessWitnessHis/busWitness_list_detail"),
      name: "businessWitnessHis_detail",
      meta: { single: true, title: "项目全周期跟踪详页", isTab: true },
    },

    //开评标实时见证列表
    {
      path: "/traddingWitness/openBidWitness/beginList",
      component: _import("modules/traddingWitness/openBidWitness/beginList"),
      name: "openBidAndEstimateWitness_beginList",
      meta: { single: true, title: "实时见证列表", isTab: true },
    },
    // 评标详页 （实时见证）
    {
      path: "/traddingWitness/openBidWitness/eval_detail",
      component: _import("modules/traddingWitness/estimateBidWitness/detail"),
      name: "estimateBidWitness_detail",
      meta: { single: true, title: "评标实时见证", isTab: true },
    },
    {
      path: "/traddingWitness/openBidWitness/eval_detail/evaluation_result",
      component: _import("modules/traddingWitness/openBidWitness/components/evaluationResult"),
      name: "estimateBidWitness_detail_evaluation_result",
      meta: { single: true, title: "查看评标报告", isTab: true },
    },
    // 开标详页 （实时见证）
    {
      path: "/traddingWitness/openBidWitness/open_detail",
      component: _import("modules/traddingWitness/openBidWitness/detail"),
      name: "openBidWitness_detail",
      meta: { single: true, title: "开标实时见证", isTab: true },
    },
    //开评标见证记录
    {
      path: "/traddingWitness/openBidWitness_his/list",
      component: _import("modules/traddingWitness/openBidWitness/list"),
      name: "openBidAndEstimateWitness_list",
      meta: { single: true, title: "开评标见证记录", isTab: true },
    },
    {
      path: "/traddingWitness/openBidWitness_his/eval_view",
      component: _import("modules/traddingWitness/estimateBidWitness/view"),
      name: "estimateBidWitness_view",
      meta: { single: true, title: "评标见证详页", isTab: true },
    },
    {
      path: "/traddingWitness/openBidWitness_his/open_view",
      component: _import("modules/traddingWitness/openBidWitness/view"),
      name: "openBidWitness_view",
      meta: { single: true, title: "开标见证详页", isTab: true },
    },

    // 门禁配置表
    {
      path: "/guard_config/list",
      component: _import("modules/config/guard_config_list"),
      name: "guard_config_list",
      meta: { single: true, title: "门禁配置列表", isTab: true },
    },
    {
      path: "/guard_config/detail",
      component: _import("modules/config/guard_config_detail"),
      name: "guard_config_detail",
      meta: { single: true, title: "门禁配置详页", isTab: true },
    },
    {
      path: "/guard_config/view",
      component: _import("modules/config/guard_config_view"),
      name: "guard_config_view",
      meta: { single: true, title: "门禁配置详页", isTab: true },
    },

    //实时门禁出入见证
    {
      path: "/realtime_guardwitness_list",
      component: _import("modules/guardWitness/realtime_guardwitness_list"),
      name: "realtime_guardwitness_list",
      meta: { single: true, title: "实时门禁出入见证", isTab: true },
    },

    //门禁出入记录
    {
      path: "/guardWitness/guard_witness_list",
      component: _import("modules/guardWitness/guard_witness_list"),
      name: "guard_witness_list",
      meta: { single: true, title: "门禁出入记录列表", isTab: true },
    },
    {
      path: "/guardWitness/guard_witness_view",
      component: _import("modules/guardWitness/guard_witness_view"),
      name: "guard_witness_view",
      meta: { single: true, title: "门禁出入记录详页", isTab: true },
    },

    //见证规则设置
    {
      path: "/witnessrule_config/list",
      component: _import("modules/config/witnessrule_config_list"),
      name: "witnessrule_config_list",
      meta: { single: true, title: "见证规则设置列表", isTab: true },
    },

    // 移动轨迹见证
    // 实时轨迹见证
    {
      path: "/track_witness",
      component: _import("modules/movingTrail/track_witness"),
      name: "track_witness",
      meta: { single: true, title: "实时轨迹", isTab: true, iframeUrl: "" },
    },
    // 历史轨迹查阅
    {
      path: "/track_history",
      component: _import("modules/movingTrail/track_history"),
      name: "track_history",
      meta: { single: true, title: "历史轨迹查阅", isTab: true, iframeUrl: "" },
    },
    // 人员轨迹追溯
    {
      path: "/track_retrospect",
      component: _import("modules/movingTrail/track_retrospect"),
      name: "track_retrospect_list",
      meta: { single: true, title: "人员轨迹追溯", isTab: true, iframeUrl: "" },
    },
    // 历史轨迹查阅
    {
      path: "/alarm_record",
      component: _import("modules/movingTrail/alarm_record"),
      name: "alarm_record",
      meta: { single: true, title: "告警记录", isTab: true, iframeUrl: "" },
    },
    // 胸牌绑定
    {
      path: "/breastpiece_bind/list",
      component: _import("modules/movingTrail/breastpiece_bind_list"),
      name: "breastpiece_bind_list",
      meta: { single: true, title: "胸牌绑定", isTab: true },
    },
    {
      path: "/track_card",
      component: _import("modules/movingTrail/track_card"),
      name: "track_card",
      meta: { single: true, title: "快速发卡", isTab: true },
    },

    //见证数据出证申请
    {
      path: "/catequest_base/list",
      component: _import("modules/catequest/catequest_base_list"),
      name: "catequest_base_list",
      meta: { single: true, title: "见证数据出证申请列表", isTab: true },
    },
    {
      path: "/catequest_base/detail",
      component: _import("modules/catequest/catequest_base_detail"),
      name: "catequest_base_detail",
      meta: { single: true, title: "见证数据出证申请详页", isTab: true },
    },

    // 待办箱，消息中心，日程管理
    {
      path: '/userTaskList',
      component: _import('modules/business/userTaskList'),
      name: 'userTaskList',
      meta: { title: '待办箱', isTab: true }
    },
    {
      path: '/messagelist',
      component: _import('modules/business/userMessage'),
      name: 'messagelist',
      meta: { title: '消息中心', isTab: true }
    },
    {
      path: '/workday',
      component: _import('modules/business/workday'),
      name: 'workday',
      meta: { title: '日程管理', isTab: true }
    },

    //见证数据出证受理
    {
      path: "/cateout_quest/list",
      component: _import("modules/catequest/cateout_quest_list"),
      name: "cateout_quest_list",
      meta: { single: true, title: "见证数据出证受理列表", isTab: true },
    },
    {
      path: "/cateout_quest/detail",
      component: _import("modules/catequest/cateout_quest_detail"),
      name: "cateout_quest_detail",
      meta: { single: true, title: "见证数据出证受理详页", isTab: true },
    },
    //制作见证书
    {
      path: "/cateout_quest/make_catequest_note",
      component: _import("modules/catequest/make_catequest_note"),
      name: "make_catequest_note",
      meta: { single: true, title: "制作见证书页", isTab: true },
    },
    //见证书下载
    {
      path: "/cateout_quest/download_catequest_note",
      component: _import("modules/catequest/download_catequest_note"),
      name: "download_catequest_note",
      meta: { single: true, title: "见证书下载页", isTab: true },
    },
    //交易见证书列表
    {
      path: "/catequest_note/list",
      component: _import("modules/catequest/catequest_note_list"),
      name: "catequest_note_list",
      meta: { single: true, title: "交易见证书", isTab: true },
    },
    //交易见证书详页
    {
      path: "/catequest_note/detail",
      component: _import("modules/catequest/catequest_note_detail"),
      name: "catequest_note_detail",
      meta: { single: true, title: "交易见证书详页", isTab: true },
    },

    //交易活动见证-项目交易活动信息
    {
      path: "/trading_activity/list",
      component: _import("modules/tradingActivity/trading_activity_list"),
      name: "trading_activity_list",
      meta: { single: true, title: "项目交易活动信息列表", isTab: true },
    },
    {
      path: "/trading_activity/detail",
      component: _import("modules/tradingActivity/trading_activity_detail"),
      name: "trading_activity_detail",
      meta: { single: true, title: "项目交易活动信息详页", isTab: true },
    },

    //交易活动见证-人员面部信息库
    {
      path: "/facial_info/list",
      component: _import("modules/facialInfo/facial_info_list"),
      name: "facial_info_list",
      meta: { single: true, title: "人员面部信息库列表", isTab: true },
    },
    {
      path: "/facial_info/detail",
      component: _import("modules/facialInfo/facial_info_detail"),
      name: "facial_info_detail",
      meta: { single: true, title: "人员面部信息详页", isTab: true },
    },

    //第三方对接系统配置列表
    {
      path: "/thirds_config/list",
      component: _import("modules/config/thirds_config_list"),
      name: "thirds_config_list",
      meta: { single: true, title: "第三方对接系统配置列表", isTab: true },
    },
    {
      path: "/thirds_config/detail",
      component: _import("modules/config/thirds_config_detail"),
      name: "thirds_config_detail",
      meta: { single: true, title: "第三方对接系统配置详页", isTab: true },
    },
    {
      path: "/thirds_config/view",
      component: _import("modules/config/thirds_config_view"),
      name: "thirds_config_view",
      meta: { single: true, title: "第三方对接系统配置详页", isTab: true },
    },

    {
      path: "/userTask",
      component: _import("modules/business/todoList"),
      name: "userTask",
      meta: { title: "待办列表", isTab: true },
    },
    {
      path: "/projectlist",
      component: _import("modules/business/projectList"),
      name: "projectlist",
      meta: { title: "项目跟踪", isTab: true, single: true },
    },

    {
      path: "/purchase/requires",
      component: _import("modules/purchase/requires/list"),
      name: "purchaseRequires",
      meta: { title: "采购需求库", isTab: true, single: true },
    },
    {
      path: "/purchase/requires_detail",
      component: _import("modules/purchase/requires/detail"),
      name: "purchaseRequiresDetail",
      meta: { single: true, title: "采购需求详情页", isTab: true },
    },
    {
      path: "/purchase/standard",
      component: _import("modules/purchase/standard/list"),
      name: "purchaseStandard",
      meta: { title: "采购需求标准", isTab: true, single: true },
    },
    {
      path: "/purchase/catalogue",
      component: _import("modules/purchase/catalogue/list"),
      name: "purchaseCatalogue",
      meta: { title: "集中采购目录", isTab: true, single: true },
    },
    {
      path: "/purchase/catalogue_config",
      component: _import("modules/purchase/catalogue/config"),
      name: "purchaseCatalogueConfig",
      meta: { single: true, title: "品目目录维护", isTab: true },
    },
    {
      path: "/purchase/catalogue_detail",
      component: _import("modules/purchase/catalogue/catalogueItemDetail"),
      name: "purchaseCatalogueDetail",
      meta: { title: "需求模板维护详页", isTab: true, single: true },
    },
    {
      path: "/purchase/lawfile",
      component: _import("modules/purchase/lawfile/list"),
      name: "lawfile",
      meta: { title: "政策法规文件", isTab: true, single: true },
    },
    {
      path: "/purchase/lawfile_detail",
      component: _import("modules/purchase/lawfile/detail"),
      name: "lawfileDetail",
      meta: { single: true, title: "政策法规文件", isTab: true },
    },
    {
      path: "/purchase/case",
      component: _import("modules/purchase/case/list"),
      name: "purchaseCase",
      meta: { title: "优秀案例", isTab: true, single: true },
    },
    {
      path: "/archive-list",
      component: _import("modules/archive/archive-list"),
      name: "archive-list",
      meta: { title: "归档列表", isTab: true, single: true },
    },
    {
      path: "/archive-detail",
      component: _import("modules/archive/archive-detail"),
      name: "archive-detail",
      meta: { title: "归档详情", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/project/list",
      component: _import("modules/singleWindowService/project/list"),
      name: "singleWindowServiceProjectList",
      meta: { title: "进场项目登记", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/project/detail",
      component: _import("modules/singleWindowService/project/detail"),
      name: "singleWindowServiceProjectDetail",
      meta: { title: "进场项目登记详情页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/project/add",
      component: _import("modules/singleWindowService/project/add"),
      name: "singleWindowServiceProjectAdd",
      meta: { title: "进场项目登记新增页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/deal/list",
      component: _import("modules/singleWindowService/deal/list"),
      name: "singleWindowServiceDealList",
      meta: { title: "成交概况登记", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/deal/add",
      component: _import("modules/singleWindowService/deal/add"),
      name: "singleWindowServiceDealAdd",
      meta: { title: "成交概况登记新增页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/deal/detail",
      component: _import("modules/singleWindowService/deal/detail"),
      name: "singleWindowServiceDealDetail",
      meta: { title: "成交概况登记详情页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/site/list",
      component: _import("modules/singleWindowService/site/list"),
      name: "singleWindowServiceSiteList",
      meta: { title: "场地预约及变更", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/site/detail",
      component: _import("modules/singleWindowService/site/detail"),
      name: "singleWindowServiceSiteDetail",
      meta: { title: "场地预约及变更详情页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/site/add",
      component: _import("modules/singleWindowService/site/add"),
      name: "singleWindowServiceSiteAdd",
      meta: { title: "场地预约新增页", isTab: true, single: true },
    },
    {
      path: "/singleWindowService/site/change",
      component: _import("modules/singleWindowService/site/change"),
      name: "singleWindowServiceSiteChange",
      meta: { title: "场地预约变更页", isTab: true, single: true },
    },
    // 招标文件检测
    {
      path: '/bidFileCheck/checkList',
      component: _import('modules/bidFileCheck/checkList/index'),
      name: 'bidFileCheckList',
      meta: {single:true, title: '招标文件检测', isTab: true}
    },
    {
      path: "/singleWindowService-tradingOverview/list",
      component: _import("modules/singleWindowService/tradingOverview/list"),
      name: "singleWindowServiceTradeOverviewList",
      meta: { title: "交易数据总览", isTab: true, single: true },
    },
    {
      path: "/singleWindowService-calendar/list",
      component: _import("modules/singleWindowService/calendar/list"),
      name: "singleWindowServiceCalendarList",
      meta: { title: "交易日历", isTab: true, single: true },
    },
  ],

  beforeEnter(to, from, next) {
    checkLogin(next);
  },
};
let proxybaseurl = process.env.VUE_APP_proxybaseurl;
if (!proxybaseurl) {
  proxybaseurl = "";
}
const router = new Router({
  mode: "history",
  base: proxybaseurl,
  scrollBehavior: () => ({ y: 0 }),
  ssoLoginUrl: "",
  isAddDynamicMenuRoutes: false, // 是否已经添加动态(菜单)路由
  routes: globalRoutes.concat(mainRoutes),
});

/**
 * 动态加载菜单流程
 * 1、判断当前是否加载菜单
 * 2、如果未加载菜单，判断当前是否需要加载动态菜单
 * 3、出现在 globalRoutes 和 skipLoadMenusRoutes 中的路由不需要加载动态菜单。
 */
router.beforeEach((to, from, next) => {
  let currentRouteType = fnCurrentRouteType(to, globalRoutes);
  if (currentRouteType !== "global") {
    currentRouteType = fnCurrentRouteType(to, skipLoadMenusRoutes);
  }

  if (
    router.options.isAddDynamicMenuRoutes ||
    (currentRouteType === "global" && !to.meta.isAuth)
  ) {
    next();
  } else {
    // checkLogin(next)
    let permsPass = false;
    initMenuList(function(result) {
      permsPass = checkPerms(to);
      if (!permsPass) logout();
      if (!result || result.code !== "1") {
        next();
      } else {
        next({ ...to, replace: true });
      }
    });
  }
});

/**
 * 检查当前路由权限
 * 根据path路径判断当前菜单路由（或者父级路由）是否存在menuList动态路由里
 * @param {*} to 当前路由
 */
function checkPerms(to) {
  if (to.path == "/" || to.path == "/home") {
    return true;
  }
  let menusList = JSON.parse(sessionStorage.getItem("menuList"));

  let path = to.path;
  // 项目见证受理详情页,左侧菜单对应父级菜单
  if (path == "/businessflowWitness/list_detail") {
    path = "/businessflowWitness_" + to.query.businessType + "/list";
  }
  // 项目见证记录详情页 左侧菜单对应父级菜单
  if (path == "/businessWitnessHis/list_detail") {
    path = "/businessWitnessHis_" + to.query.businessType + "/list";
  }
  let obj = recursion(menusList, path);

  if (obj != null) {
    return true;
  } else {
    return false;
  }
}
/**
 * 递归查询字路由path参数 是否存在menuList动态路由里
 * 根据path路径判断当前菜单路由（或者父级路由）
 * @param {*} data 路由菜单
 * @param {*} path 当前路由路径
 */
function recursion(data, path) {
  if (!data) {
    return;
  }
  // 从 path中拿到菜单目录
  let pathArr = path.split("/");
  let activePathName =
    pathArr.length > 2 ? pathArr[pathArr.length - 2] : pathArr[1] || pathArr[0];
  let result = null;

  for (var i in data) {
    if (result !== null) {
      break;
    }
    var item = data[i];

    // type == 1 代表菜单地址
    if (item.type == 1) {
      let routerUrlArr = item.url.split("/");
      let routerName =
        routerUrlArr.length > 2
          ? routerUrlArr[pathArr.length - 2]
          : routerUrlArr[1] || routerUrlArr[0];
      if (routerName == activePathName) {
        result = item;
        break;
      }
    } else if (item.list && item.list.length > 0) {
      result = recursion(item.list, path);
    }
  }
  return result;
}

/**
 * 判断当前路由类型, global: 全局路由, main: 主入口路由
 * 判断逻辑：
 *    1、如果目标路由的name 或路径 出现在 globalRoutes 参数中
 * @param {*} route 当前路由
 * @param {*} globalRoutes 路由匹配数组
 */
function fnCurrentRouteType(route, globalRoutes = []) {
  var temp = [];
  for (var i = 0; i < globalRoutes.length; i++) {
    if (
      route.path === globalRoutes[i].path ||
      (route.name !== undefined && route.name === globalRoutes[i].name)
    ) {
      return "global";
    } else if (
      globalRoutes[i].children &&
      globalRoutes[i].children.length >= 1
    ) {
      temp = temp.concat(globalRoutes[i].children);
    }
  }
  return temp.length >= 1 ? fnCurrentRouteType(route, temp) : "main";
}

export function initMenuList(callback) {
  if (router.options.isAddDynamicMenuRoutes === true) {
    if (callback !== undefined) {
      callback();
    }
    return;
  }
  loginHttp({
    url: "/loadMenus",
    method: "post",
  })
    .then(({ data }) => {
      if (data && data.code === "1") {
        fnAddDynamicMenuRoutes(data.menuList);
        router.options.isAddDynamicMenuRoutes = true;
        sessionStorage.setItem(
          "menuList",
          JSON.stringify(data.menuList || "[]")
        );
        sessionStorage.setItem(
          "permissions",
          JSON.stringify(data.permissions || "[]")
        );
      } else {
        sessionStorage.setItem("menuList", "[]");
        sessionStorage.setItem("permissions", "[]");
      }
      if (callback !== undefined) {
        callback(data);
      }
    })
    .catch((e) => {
      console.log(
        `%c${e} 请求菜单列表和权限失败，跳转至登录页！！`,
        "color:blue"
      );
      sessionStorage.setItem("menuList", "[]");
      sessionStorage.setItem("permissions", "[]");
      router.push({ name: "error" });
    });
}

/**
 * 添加动态(菜单)路由
 * @param {*} menuList 菜单列表
 * @param {*} routes 递归创建的动态(菜单)路由
 */
function fnAddDynamicMenuRoutes(menuList = []) {
  let routes = [];
  fnAddDynamicMenuRoutesCicle(menuList, routes);
  mainRoutes.name = "main-dynamic";
  mainRoutes.children = routes;
  router.addRoutes([mainRoutes, { path: "*", redirect: { name: "404" } }]);
  sessionStorage.setItem(
    "dynamicMenuRoutes",
    JSON.stringify(mainRoutes.children || "[]")
  );
  console.log("\n");
  console.log(
    "%c!<-------------------- 动态(菜单)路由 s -------------------->",
    "color:blue"
  );
  console.log(mainRoutes.children);
  console.log(
    "%c!<-------------------- 动态(菜单)路由 e -------------------->",
    "color:blue"
  );
}

/**
 * 添加动态(菜单)路由
 * @param {*} menuList 菜单列表
 * @param {*} routes 递归创建的动态(菜单)路由
 */
function fnAddDynamicMenuRoutesCicle(menuList = [], routes = []) {
  var temp = [];
  let funcList = [];
  for (var i = 0; i < menuList.length; i++) {
    let menuItem = menuList[i];
    if (menuItem.list && menuItem.list.length >= 1) {
      temp = temp.concat(menuItem.list);
    } else if (menuItem.url && /\S/.test(menuItem.url)) {
      if (menuItem.funcList && menuItem.funcList.length >= 1) {
        funcList = funcList.concat(menuItem.funcList);
      }
      //有明确路由名称的表示静态路由
      if (menuItem.routeName) {
        continue;
      }
      var route = {}

      // 如果是businessnodes 开头的路由，需要特殊处理
      if (menuItem.url.indexOf("businessnodes") > -1) {
        // debugger
        route = {
          path: menuItem.url,
          component: _import("modules/businessnodes/businessNodesList"),
          name: menuItem.url,
          meta: {
            menuId: menuItem.menuId,
            title: menuItem.name,
            isDynamic: true,
            isTab: true,
            iframeUrl: "",
          },
        };
        routes.push(route)
      } else {

        menuItem.url = menuItem.url.replace(/^\//, "");
        let url = menuItem.url;
        let argIndex = url.indexOf("?");
        if (argIndex > 0) {
          url = url.substr(0, argIndex);
        }
        let routePath = url.replace("/", "-");
        route = {
          path: routePath,
          component: null,
          name: routePath,
          meta: {
            menuId: menuItem.menuId,
            title: menuItem.name,
            isDynamic: true,
            isTab: true,
            iframeUrl: "",
          },
        };
        // url以http[s]://开头, 通过iframe展示
        if (isURL(menuItem.url)) {
          route["path"] = `i-${menuItem.menuId}`;
          route["name"] = `i-${menuItem.menuId}`;
          route["meta"]["iframeUrl"] = menuItem.url;
        } else {
          try {
            route["component"] = _import(`modules/${url}`) || null;
          } catch (e) {}
        }
        routes.push(route);
      }
    }
  }
  if (temp.length >= 1) {
    fnAddDynamicMenuRoutesCicle(temp, routes);
  }
  if (funcList.length >= 1) {
    fnAddDynamicMenuRoutesCicle(funcList, routes);
  }
}

export default router;
